'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { Badge, LinkButton } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  gridItemVariants,
  headerVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import { CodeWindowBase } from '../code-window'

/** Single card with code window and content */
export interface CodeCard {
  badge?: string
  heading: string
  /** Card description - supports Markdown (bold, code, links) */
  description: string
  learnMore?: { text: string; url: string }
  code: {
    code: string
    language: string
  }
}

export interface FeaturesCodeCardsProps {
  /** Section id for anchor links */
  id?: string
  /** Small label above heading */
  label?: string
  /** Section heading */
  heading?: string
  /** Section description */
  description?: string
  /** Number of columns (2 or 4, default 2) */
  columns?: 2 | 4
  /** Array of cards (typically 2 or 4) */
  cards?: CodeCard[]
  className?: string
}

const defaultCards: CodeCard[] = [
  {
    badge: 'New',
    heading: 'Copy paste components',
    description: 'Production-ready components that you can copy and paste into your projects. Fully typed and accessible.',
    learnMore: { text: 'Learn more', url: '#' },
    code: {
      code: `import { Button } from '@mdxui/primitives'

export function MyButton() {
  return (
    <Button variant="primary">
      Click me
    </Button>
  )
}`,
      language: 'typescript',
    },
  },
  {
    heading: '100% customizable',
    description: 'Every component is built with Tailwind CSS and can be customized to match your brand.',
    learnMore: { text: 'Learn more', url: '#' },
    code: {
      code: `import { Card } from '@mdxui/primitives'

export function MyCard() {
  return (
    <Card className="p-6">
      <h2>Hello World</h2>
    </Card>
  )
}`,
      language: 'typescript',
    },
  },
]

/**
 * FeaturesCodeCards - Grid with code windows and text content
 * Code windows are borderless with copy button
 * Supports 2 or 4 column layouts with optional header
 */
const FeaturesCodeCards = ({
  id,
  label,
  heading,
  description,
  columns = 2,
  cards = defaultCards,
  className,
}: FeaturesCodeCardsProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants
  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants

  const hasHeader = label || heading || description

  const gridClasses = columns === 4
    ? 'grid gap-8 grid-cols-1 sm:grid-cols-2 lg:gap-10'
    : 'grid gap-10 lg:grid-cols-2 lg:gap-20'

  return (
    <motion.section
      id={id}
      ref={sectionRef}
      className={cn('py-16 sm:py-24 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        {/* Optional Header */}
        {hasHeader && (
          <motion.div
            className="mx-auto mb-12 flex max-w-2xl flex-col items-center gap-4 text-center"
            variants={headerAnimation}
          >
            {label && (
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                {label}
              </p>
            )}
            {heading && (
              <h2 className="text-3xl font-semibold sm:text-4xl">{heading}</h2>
            )}
            {description && (
              <Markdown size="base">{description}</Markdown>
            )}
          </motion.div>
        )}

        <div className={gridClasses}>
          {cards.map((card) => (
            <motion.div key={card.heading} variants={itemAnimation} className="min-w-0">
              <div className="rounded-[var(--radius)] border border-border overflow-x-auto shadow-sm">
                <CodeWindowBase
                  code={card.code.code}
                  language={card.code.language}
                  showHeader={false}
                  showCopy={true}
                  theme="auto"
                  style="borderless"
                  className="sm:aspect-video sm:max-h-[500px] bg-card"
                />
              </div>
              <div className="min-w-0 px-2 sm:px-4 py-6">
                <div className="mb-1 flex min-w-0 items-center gap-2 font-semibold">
                  <span className="min-w-0 break-words">{card.heading}</span>
                  {card.badge && <Badge className="shrink-0">{card.badge}</Badge>}
                </div>
                <div className="prose prose-sm max-w-none text-muted-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                  <ReactMarkdown>{card.description}</ReactMarkdown>
                </div>
                {card.learnMore && (
                  <LinkButton href={card.learnMore.url} className="mt-4">
                    {card.learnMore.text}
                  </LinkButton>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  )
}

export { FeaturesCodeCards }
